import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon
  const [token, setToken] = useState(localStorage.getItem("izzo_token"));
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    async function check() {
      if (!token) {
        setUser(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        localStorage.removeItem("izzo_token");
        setUser(false);
      }
    }
    check();
  }, [token]);

  // White-label: applica il tema dello studio dell'utente dopo il login
  useEffect(() => {
    const hexToRgb = (h) => {
      const x = h.replace("#", "");
      return `${parseInt(x.slice(0, 2), 16)} ${parseInt(x.slice(2, 4), 16)} ${parseInt(x.slice(4, 6), 16)}`;
    };
    const apply = (b) => {
      const r = document.documentElement.style;
      const keys = ["--brand-rgb", "--brand-primary", "--brand-dark-rgb", "--brand-primary-dark"];
      if (b?.custom && b.colore) {
        r.setProperty("--brand-rgb", hexToRgb(b.colore));
        r.setProperty("--brand-primary", b.colore);
        r.setProperty("--brand-dark-rgb", hexToRgb(b.colore_scuro || b.colore));
        r.setProperty("--brand-primary-dark", b.colore_scuro || b.colore);
      } else {
        keys.forEach((k) => r.removeProperty(k));
      }
      document.body.classList.toggle("brand-custom", !!b?.custom);
      const mlink = document.querySelector('link[rel="manifest"]');
      if (mlink) {
        // Iter122df · usa sempre il manifest per-tenant quando c'è un branding con
        // tenant_id, così ogni Studio (anche senza colore/logo custom) vede il proprio
        // nome sull'icona iOS/Android una volta installata la PWA. Fallback al manifest
        // statico neutro `/manifest.json` per la landing pubblica (utente non loggato).
        mlink.href = b?.tenant_id
          ? `${process.env.REACT_APP_BACKEND_URL}/api/manifest/${b.tenant_id}.json`
          : "/manifest.json";
      }
    };
    if (!user || user === false) { apply(null); setBranding(null); return; }
    api.get("/branding")
      .then(({ data }) => { setBranding(data); apply(data); })
      .catch(() => {});
  }, [user]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    // Iter122bt · multi-account: se il backend ritorna una lista di account, delego al chiamante
    if (data.multi_account) {
      return { multi_account: true, accounts: data.accounts, identifier, password };
    }
    localStorage.setItem("izzo_token", data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const loginSelect = async (email, password, user_id) => {
    const { data } = await api.post("/auth/login/select", { email, password, user_id });
    localStorage.setItem("izzo_token", data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const activate = async (login_id, activation_code, password, privacy_consenso) => {
    const { data } = await api.post("/auth/activate", { login_id, activation_code, password, privacy_consenso });
    localStorage.setItem("izzo_token", data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("izzo_token", data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("izzo_token");
    setToken(null);
    setUser(false);
  };

  // Iter122de · Ricarica /auth/me senza fare logout — usato dopo update di campi
  // tenant-level (es. Profilo Studio) per riflettere subito lo stato in UI (banner).
  const refresh = async () => {
    if (!token) return;
    try { const { data } = await api.get("/auth/me"); setUser(data); return data; }
    catch { /* mantieni sessione corrente in caso di errore transitorio */ }
  };

  return (
    <AuthContext.Provider value={{ user, branding, login, loginSelect, register, activate, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
